import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { IconBell } from '@tabler/icons-react';
import { FormattedMessage } from 'react-intl';
import {
  getMyNotifications,
  getMyUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationsRead
} from 'api/r2m';
import type { EventNotification, EmailSeverity } from 'types/r2m';

const SEVERITY_COLOR: Record<EmailSeverity, string> = {
  low: '#9e9e9e',
  medium: '#ed6c02',
  high: '#d32f2f'
};

function relativeHu(iso: string): string {
  const now = Date.now();
  const d = new Date(iso).getTime();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'pár másodperce';
  if (diff < 3600) return `${Math.floor(diff / 60)} perce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} órája`;
  return `${Math.floor(diff / 86400)} napja`;
}

export default function NotificationBell() {
  const theme = useTheme();
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const { data: countData, mutate: mutateCount } = useSWR(
    '/my/notifications/unread-count',
    () => getMyUnreadNotificationCount(),
    { refreshInterval: 30_000, revalidateOnFocus: true, fallbackData: 0 }
  );
  const unreadCount = countData ?? 0;

  const { data: items, error: itemsError, isLoading: itemsLoading, mutate: mutateList } = useSWR<EventNotification[]>(
    open ? '/my/notifications?limit=20' : null,
    () => getMyNotifications({ limit: 20 })
  );

  const badge = useMemo(() => (unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : null), [unreadCount]);

  const handleToggle = () => setOpen((v) => !v);
  const handleClose = () => setOpen(false);

  const handleItemClick = async (n: EventNotification) => {
    if (!n.readAt) {
      try {
        await markNotificationsRead([n.id]);
        mutateCount();
        mutateList();
      } catch {
        // ignore — navigation should not be blocked by mark-read failures
      }
    }
    setOpen(false);
    const goesToOffersTab = n.action?.startsWith('offer_');
    navigate(`/app/events/${n.eventId}${goesToOffersTab ? '?tab=offers' : ''}`);
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead();
    mutateCount();
    mutateList();
  };

  return (
    <>
      <Box ref={anchorRef} sx={{ ml: 1, mr: 1 }}>
        <Badge badgeContent={badge ?? 0} color="error" invisible={!badge} overlap="circular">
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              color: theme.vars?.palette?.secondary?.dark,
              background: theme.vars?.palette?.secondary?.light,
              cursor: 'pointer'
            }}
            onClick={handleToggle}
          >
            <IconBell stroke={1.5} size="20px" />
          </Avatar>
        </Badge>
      </Box>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        disablePortal={false}
        style={{ zIndex: 1300 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={8} sx={{ width: 380, maxHeight: 500, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1">
                <FormattedMessage id="notifications.title" defaultMessage="Értesítések" />
              </Typography>
              {unreadCount > 0 && (
                <Button size="small" onClick={handleMarkAll}>
                  <FormattedMessage id="notifications.markAllRead" defaultMessage="Mind olvasott" />
                </Button>
              )}
            </Box>
            <Divider />
            <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
              {itemsLoading || (items === undefined && !itemsError) ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="notifications.loading" defaultMessage="Betöltés…" />
                  </Typography>
                </Box>
              ) : itemsError ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                    <FormattedMessage id="notifications.error" defaultMessage="Nem sikerült betölteni az értesítéseket." />
                  </Typography>
                  <Button size="small" onClick={() => mutateList()}>
                    <FormattedMessage id="notifications.retry" defaultMessage="Próbáld újra" />
                  </Button>
                </Box>
              ) : items === undefined || items.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="notifications.empty" defaultMessage="Nincs értesítés." />
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {items.map((n) => (
                    <ListItemButton
                      key={n.id}
                      onClick={() => handleItemClick(n)}
                      sx={{
                        borderLeft: `4px solid ${SEVERITY_COLOR[n.severity]}`,
                        background: n.readAt ? 'transparent' : 'rgba(25,118,210,0.06)'
                      }}
                    >
                      <ListItemText
                        primary={n.summary}
                        secondary={
                          <>
                            <Typography variant="caption" color="text.secondary">
                              {relativeHu(n.createdAt)}
                              {n.event?.name ? ` · ${n.event.name}` : ''}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
